import React from "react";
import {
  ContentLayout,
  CustomDetailsList,
  Loader,
  CustomTable,
  HeaderBreadcrumbs,
} from "../../../shared";
import { Chip, Box } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useCustomerStampCardDetails } from "../hooks/useCustomerStampCardDetails";

export const CustomerStampCardDetails = () => {
  const navigate = useNavigate();
  const { id, stampCardId } = useParams();

  const { state } = useCustomerStampCardDetails({
    customerId: id,
    stampCardId: stampCardId,
  });

  const getStatusStyle = (statusValue) => {
    const styles = {
      1: { backgroundColor: "#FFF4CE", color: "#C07302" }, // Ongoing
      2: { backgroundColor: "#FFE7E3", color: "#C42B1C" }, // Expired
      3: { backgroundColor: "#0000000F", color: "#A3A3A3" }, // Inactive
      4: { backgroundColor: "#E1FBF2", color: "#0F875B" }, // Completed
    };
    return (
      styles[statusValue] || {
        backgroundColor: "#eee",
        color: "#666",
      }
    );
  };

  const detailsColumns = [
    {
      title: "Stamp Card Name",
      accessor: (row) => row.name || "--",
    },
    {
      title: "Cycle",
      accessor: (row) => row.cycle || "--",
    },
    {
      title: "No of Stamps",
      accessor: (row) => (
        <Chip
          label={`${row.stamps_earned || 0}/${row.total_stamps || 0}`}
          color="primary"
          size="small"
          sx={{
            bgcolor: "#001A52",
            "& .MuiChip-label": {},
          }}
          disabled={false}
        />
      ),
    },
    {
      title: "Cycle Validity (Days)",
      accessor: (row) => row.cycle_validity || "--",
    },
    {
      title: "Started On",
      accessor: (row) => row.started_on || "--",
    },
    {
      title: "End Date",
      accessor: (row) => row.ended_on || "--",
    },
    {
      title: "Status",
      accessor: (row) => {
        return row.status_label ? (
          <Chip
            label={row.status_label}
            size="small"
            sx={getStatusStyle(row.status)}
          />
        ) : (
          "--"
        );
      },
    },
  ];

  const getProgressStatusStyle = (statusValue) => {
    const styles = {
      1: { backgroundColor: "#FFF4CE", color: "#C07302" }, // Collected
      2: { backgroundColor: "#1560D414", color: "#1560D4" }, // Unlocked
      3: { backgroundColor: "#E1FBF2", color: "#0F875B" }, // Redeemed
      4: { backgroundColor: "#FFE7E3", color: "#C42B1C" }, // Expired
    };
    return (
      styles[statusValue] || {
        backgroundColor: "#eee",
        color: "#666",
      }
    );
  };

  // Stamp Progress table columns
  const stampProgressColumns = React.useMemo(
    () => [
      {
        Header: "Steps",
        accessor: "type",
        Cell: ({ value, row }) => {
          if (value === "reward" && row.original.stamp_reward_name) {
            return `${row.original.type_label || value} - ${row.original.stamp_reward_name}`;
          }
          return row.original.type_label || value || "--";
        },
        disableSortBy: true,
      },
      {
        Header: "Status",
        accessor: "status_label",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          const statusStyle = getProgressStatusStyle(row.original.status);
          return value ? (
            <Chip
              label={value}
              size="small"
              sx={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
              }}
            />
          ) : (
            "--"
          );
        },
      },
      {
        Header: "Order Number",
        accessor: "order_number",

        Cell: ({ value, row }) => {
          return (
            <Box
              component="span"
              sx={{
                color: "#000",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              onClick={() =>
                navigate(`/orders/details/${row.original.order_id}`)
              }
            >
              {value || "--"}
            </Box>
          );
        },
      },
      {
        Header: "Order Date",
        accessor: "order_date",
        disableSortBy: true,
        Cell: ({ value }) => value || "--",
      },
    ],
    [navigate],
  );

  return (
    <ContentLayout
      title={state.data.name || "Stamp Card Details"}
      breadcrumbs={
        <HeaderBreadcrumbs
          links={[
            { name: "Dashboard", href: "/" },
            { name: "Customers", href: "/customers" },
            {
              name: "Customer Details",
              href: `/customers/details/${id}`,
            },
            {
              name: state.data.name || "Stamp Card Details",
            },
          ]}
        />
      }
    >
      {state.isLoading ? (
        <Loader />
      ) : (
        <Box>
          <CustomDetailsList
            columns={detailsColumns}
            data={state.data}
            title="Details"
          />
          <CustomTable
            columns={stampProgressColumns}
            data={state.data.transactions || []}
            searchBar={false}
            tableTitle="Stamp Progress"
            rowsSelection={false}
            pagination={false}
            disa
          />
        </Box>
      )}
    </ContentLayout>
  );
};
